import React from 'react'
import styled from 'styled-components'
import themeGet, {FuegokitThemeMapType} from '@fuegokit/react/utils/theme-get'
import Box from '@fuegokit/react/components/Box'
import VisuallyHidden from '@fuegokit/react/components/VisuallyHidden'
import sx, {SxProp} from '@fuegokit/react/sx'
import {
  AkRbActionIcon,
  AkRbAssignIcon,
  AkRbAttachmentIcon,
  AkRbBugIcon,
  AkRbCommentIcon,
  AkRbConnectionIcon,
  AkRbDeprecatedIcon,
  AkRbEpicIcon,
  AkRbErrorIcon,
  AkRbFieldIcon,
  AkRbFiltersIcon,
  AkRbHierarchyIcon,
  AkRbIssueIcon,
  AkRbLinkIcon,
  AkRbMailIcon,
  AkRbMessageIcon,
  AkRbMiscIcon,
  AkRbOtherIcon,
  AkRbParentIcon,
  AkRbProjectIcon,
  AkRbScreenIcon,
  AkRbScriptIcon,
  AkRbSettingsIcon,
  AkRbSiblingIcon,
  AkRbStatusIcon,
  AkRbStoryIcon,
  AkRbSubtaskIcon,
  AkRbSyncIcon,
  AkRbTaskIcon,
  AkRbTimeIcon,
  AkRbTriggerIcon,
  AkRbTypeIcon,
  AkRbUserIcon,
  AkRbUsersIcon,
  AkRbWorklogIcon
} from '@fuegokit/fuegoicons-react'

import {TestIdType} from '@fuegokit/react/experimental/RuleBuilder/types'

type RuleBuilderIconSize = 'small' | 'medium' | 'large' | 'default'

export const ruleBuilderIconsMap = {
  action: AkRbActionIcon,
  assign: AkRbAssignIcon,
  attachment: AkRbAttachmentIcon,
  bug: AkRbBugIcon,
  comment: AkRbCommentIcon,
  connection: AkRbConnectionIcon,
  deprecated: AkRbDeprecatedIcon,
  epic: AkRbEpicIcon,
  error: AkRbErrorIcon,
  field: AkRbFieldIcon,
  filters: AkRbFiltersIcon,
  hierarchy: AkRbHierarchyIcon,
  issue: AkRbIssueIcon,
  link: AkRbLinkIcon,
  mail: AkRbMailIcon,
  message: AkRbMessageIcon,
  misc: AkRbMiscIcon,
  other: AkRbOtherIcon,
  parent: AkRbParentIcon,
  project: AkRbProjectIcon,
  screen: AkRbScreenIcon,
  script: AkRbScriptIcon,
  settings: AkRbSettingsIcon,
  sibling: AkRbSiblingIcon,
  status: AkRbStatusIcon,
  story: AkRbStoryIcon,
  subtask: AkRbSubtaskIcon,
  sync: AkRbSyncIcon,
  task: AkRbTaskIcon,
  time: AkRbTimeIcon,
  trigger: AkRbTriggerIcon,
  type: AkRbTypeIcon,
  user: AkRbUserIcon,
  users: AkRbUsersIcon,
  worklog: AkRbWorklogIcon
}

export type RuleBuilderIconType = keyof typeof ruleBuilderIconsMap

type RuleBuilderIconComponentType = {
  /**
   * The id of the Rule Builder icon to render.
   * Refer to @fuegokit.design/fuegoicons and search for icons prefixed with `ak-rb-`.
   */
  iconId: RuleBuilderIconType
  /**
   * Unique id
   */
  id?: string
  /**
   * Size of the Rule Builder Icon
   * @default 'default'
   */
  size?: RuleBuilderIconSize
  /**
   * Determines whether to apply the bold style or not.
   */
  isBold?: boolean
  /**
   * Change the style whether the icon is associated with a disabled rule or element
   */
  isDisabled?: boolean
  /**
   * The aria-label to pass. If no aria-label is passed, the iconId will be applied to the wrapper. See: https://css-tricks.com/accessible-svg-icons/
   */
  ariaLabel?: string
}

export type RuleBuilderIconProps = RuleBuilderIconComponentType & TestIdType & SxProp
type RuleBuilderWrapperAppearanceObject = {
  [K in RuleBuilderIconType]: FuegokitThemeMapType
}
export const ruleBuilderWrapperAppearanceObject: {bold: FuegokitThemeMapType; default: FuegokitThemeMapType} = {
  bold: {
    action: 'colors.background.accent.purple.bolder',
    assign: 'colors.background.accent.yellow.bolder',
    attachment: 'colors.background.accent.magenta.bolder',
    bug: 'colors.background.accent.red.bolder',
    comment: 'colors.background.accent.green.bolder',
    connection: 'colors.background.accent.teal.bolder',
    deprecated: 'colors.background.accent.red.bolder',
    epic: 'colors.background.accent.purple.bolder',
    error: 'colors.background.accent.red.bolder',
    field: 'colors.background.accent.teal.bolder',
    filters: 'colors.background.accent.green.bolder',
    hierarchy: 'colors.background.accent.orange.bolder',
    issue: 'colors.background.accent.blue.bolder',
    link: 'colors.background.accent.magenta.bolder',
    mail: 'colors.background.accent.purple.bolder',
    message: 'colors.background.accent.teal.bolder',
    misc: 'colors.background.accent.teal.bolder',
    other: 'colors.background.accent.gray.bolder',
    parent: 'colors.background.accent.blue.bolder',
    project: 'colors.background.accent.purple.bolder',
    screen: 'colors.background.accent.teal.bolder',
    script: 'colors.background.accent.purple.bolder',
    settings: 'colors.background.accent.orange.bolder',
    sibling: 'colors.background.accent.blue.bolder',
    status: 'colors.background.accent.green.bolder',
    story: 'colors.background.accent.green.bolder',
    subtask: 'colors.background.accent.blue.bolder',
    sync: 'colors.background.accent.green.bolder',
    task: 'colors.background.accent.blue.bolder',
    time: 'colors.background.accent.orange.bolder',
    trigger: 'colors.background.accent.red.bolder',
    type: 'colors.background.accent.blue.bolder',
    user: 'colors.background.accent.yellow.bolder',
    users: 'colors.background.accent.green.bolder',
    worklog: 'colors.background.accent.orange.bolder'
  },
  default: {
    action: 'colors.background.accent.purple.subtle',
    assign: 'colors.background.accent.yellow.subtle',
    attachment: 'colors.background.accent.magenta.subtle',
    bug: 'colors.background.accent.red.subtle',
    comment: 'colors.background.accent.green.subtle',
    connection: 'colors.background.accent.teal.subtle',
    deprecated: 'colors.background.accent.red.subtle',
    epic: 'colors.background.accent.purple.subtle',
    error: 'colors.background.accent.red.subtle',
    field: 'colors.background.accent.teal.subtle',
    filters: 'colors.background.accent.green.subtle',
    hierarchy: 'colors.background.accent.orange.subtle',
    issue: 'colors.background.accent.blue.subtle',
    link: 'colors.background.accent.magenta.subtle',
    mail: 'colors.background.accent.purple.subtle',
    message: 'colors.background.accent.teal.subtle',
    misc: 'colors.background.accent.teal.subtle',
    other: 'colors.background.accent.gray.subtle',
    parent: 'colors.background.accent.blue.subtle',
    project: 'colors.background.accent.purple.subtle',
    screen: 'colors.background.accent.teal.subtle',
    script: 'colors.background.accent.purple.subtle',
    settings: 'colors.background.accent.orange.subtle',
    sibling: 'colors.background.accent.blue.subtle',
    status: 'colors.background.accent.green.subtle',
    story: 'colors.background.accent.green.subtle',
    subtask: 'colors.background.accent.blue.subtle',
    sync: 'colors.background.accent.green.subtle',
    task: 'colors.background.accent.blue.subtle',
    time: 'colors.background.accent.orange.subtle',
    trigger: 'colors.background.accent.red.subtle',
    type: 'colors.background.accent.blue.subtle',
    user: 'colors.background.accent.yellow.subtle',
    users: 'colors.background.accent.green.subtle',
    worklog: 'colors.background.accent.orange.subtle'
  }
}

const paddingObject: Record<RuleBuilderIconSize, string> = {
  small: `3px 4px`,
  default: `0px 0px`,
  medium: `0px 0px`,
  large: `0px 0px`
}
const sizeObject: Record<RuleBuilderIconSize, number> = {
  small: 16,
  default: 24,
  medium: 32,
  large: 48
}

const Wrapper = styled(Box)<RuleBuilderIconProps>`
  display: inline-flex;
  border-radius: ${p => (p.size === 'small' ? themeGet('radii.1') : themeGet('radii.2'))};
  padding: ${p => (p.size ? paddingObject[p.size as RuleBuilderIconSize] : '0')};
  background-color: ${p => {
    if (p.isDisabled) {
      return themeGet('colors.background.disabled')
    }
    return p.isBold
      ? themeGet(ruleBuilderWrapperAppearanceObject.bold[p.iconId])
      : themeGet(ruleBuilderWrapperAppearanceObject.default[p.iconId])
  }};
  color: ${p => (p.isDisabled ? themeGet('colors.icon.disabled') : themeGet('colors.icon.inverse'))};

  ${sx}
`

const RuleBuilderIcon = ({
  iconId,
  id,
  testId,
  isBold = false,
  size = 'default',
  ariaLabel = iconId,
  isDisabled,
  ...props
}: RuleBuilderIconProps) => {
  const Component = ruleBuilderIconsMap[iconId]
  return (
    <Wrapper
      as="div"
      iconId={iconId}
      size={size}
      isBold={isBold}
      isDisabled={isDisabled}
      data-testid={testId && `${testId}-wrapper`}
      // since the icon is purely decorative, we place the aria-label on the wrapper element, not the svg, and we set aria-hidden to true on the svg
      // see: "eye-candy" --> https://www.w3.org/WAI/tutorials/images/decorative/
      // and: https://css-tricks.com/accessible-svg-icons/
      aria-label={ariaLabel}
      {...props}
    >
      <VisuallyHidden>{iconId}</VisuallyHidden>
      <Component
        size={sizeObject[size as RuleBuilderIconSize]}
        data-testid={testId && `${testId}-icon`}
        aria-hidden="true"
      />
    </Wrapper>
  )
}

export default RuleBuilderIcon
